/**
 * Admin new-org provisioning — two paths from one form:
 *  - "Issue comped license" (adminIssueComped) → on success shows the owner acceptUrl.
 *  - "Generate checkout link" (adminCheckoutLink) → on success shows the Stripe checkout url.
 * Both require ownerEmail; ownerName/plan/seats apply to the comped path.
 */
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { adminIssueComped, adminCheckoutLink, AdminApiError } from '../../../src/api/adminClient';
import { Screen, Text, Card, Field, Button } from '../../../src/components/ui';
import { copyText } from '../../../src/components/admin/clipboard';
import { AdminHeader } from '../../../src/components/admin/AdminHeader';
import { theme } from '../../../src/theme';

export default function AdminNewOrgScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [plan, setPlan] = useState('');
  const [seats, setSeats] = useState('');

  const [busy, setBusy] = useState<'comp' | 'checkout' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [compedResult, setCompedResult] = useState<{ message: string; acceptUrl?: string } | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  function validEmail(): string | null {
    const trimmed = ownerEmail.trim();
    if (!trimmed) {
      setError('Owner email is required.');
      return null;
    }
    return trimmed;
  }

  function parseSeats(): number | undefined {
    const t = seats.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isInteger(n) && n > 0 ? n : undefined;
  }

  async function handleIssueComped() {
    const email = validEmail();
    if (!email) return;
    setError(null);
    setCompedResult(null);
    setCheckoutUrl(null);
    setBusy('comp');
    try {
      const res = await adminIssueComped({
        ownerEmail: email,
        ownerName: ownerName.trim() || undefined,
        plan: plan.trim() || undefined,
        seats: parseSeats(),
      });
      setCompedResult({
        message: res.alreadyProvisioned
          ? 'This owner was already provisioned — returning the existing license.'
          : 'Comped license issued.',
        acceptUrl: res.acceptUrl,
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    } catch (err) {
      setError(err instanceof AdminApiError || err instanceof Error ? err.message : 'Failed to issue comped license.');
    } finally {
      setBusy(null);
    }
  }

  async function handleCheckoutLink() {
    const email = validEmail();
    if (!email) return;
    setError(null);
    setCompedResult(null);
    setCheckoutUrl(null);
    setBusy('checkout');
    try {
      const res = await adminCheckoutLink({ ownerEmail: email });
      setCheckoutUrl(res.url);
    } catch (err) {
      setError(err instanceof AdminApiError || err instanceof Error ? err.message : 'Failed to generate checkout link.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Screen scroll maxWidth={560}>
      <AdminHeader active="orgs" />

      <Text variant="small" color="link" onPress={() => router.replace('/admin/orgs')} accessibilityRole="button" style={styles.back}>
        ← Back to organizations
      </Text>

      <Card>
        <Text variant="title">Provision an organization</Text>
        <Field
          label="Owner email (required)"
          value={ownerEmail}
          onChangeText={setOwnerEmail}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          placeholder="owner@example.com"
          editable={busy === null}
        />
        <Field label="Owner name" value={ownerName} onChangeText={setOwnerName} placeholder="Jane Contractor" editable={busy === null} />
        <Field label="Plan" value={plan} onChangeText={setPlan} autoCapitalize="none" placeholder="e.g. pro" editable={busy === null} />
        <Field label="Seats" value={seats} onChangeText={setSeats} keyboardType="number-pad" placeholder="e.g. 5" editable={busy === null} />

        {error ? <Text color="danger">{error}</Text> : null}

        <View style={styles.actions}>
          <Button
            title="Issue comped license"
            size="md"
            fullWidth={false}
            loading={busy === 'comp'}
            disabled={busy !== null}
            onPress={handleIssueComped}
          />
          <Button
            title="Generate checkout link"
            size="md"
            variant="secondary"
            fullWidth={false}
            loading={busy === 'checkout'}
            disabled={busy !== null}
            onPress={handleCheckoutLink}
          />
        </View>
      </Card>

      {compedResult ? (
        <Card>
          <Text variant="bodyStrong" color="link">
            {compedResult.message}
          </Text>
          {compedResult.acceptUrl ? (
            <>
              <Text variant="label" color="secondary">
                Owner accept URL
              </Text>
              <Text variant="data" selectable>
                {compedResult.acceptUrl}
              </Text>
              <Button title="Copy accept URL" size="sm" variant="secondary" fullWidth={false} onPress={() => void copyText(compedResult.acceptUrl ?? '')} />
            </>
          ) : (
            <Text variant="small" color="secondary">
              No accept URL was returned (owner may already have an active account).
            </Text>
          )}
        </Card>
      ) : null}

      {checkoutUrl ? (
        <Card>
          <Text variant="bodyStrong" color="link">
            Checkout link generated.
          </Text>
          <Text variant="data" selectable>
            {checkoutUrl}
          </Text>
          <Button title="Copy checkout link" size="sm" variant="secondary" fullWidth={false} onPress={() => void copyText(checkoutUrl)} />
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  back: { paddingVertical: theme.spacing.xs },
  actions: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap', paddingTop: theme.spacing.sm },
});
